import { faqImages, type FAQImageKey } from "@/assets/faq";
import styles from "@/shared/components/FAQ/FAQ.module.css";

/**
 * FAQ Text Formatter
 * Syntax:
 *   [key]         = inline image
 *   [key:block]   = block image
 *   [key:sm]      = small block image
 *   [key:lgBlock] = large block image
 *   [key:side]    = side-by-side image (use with arrow between)
 *   **text**      = bold
 *   __text__      = underline
 */

const PATTERN = /(\[[^\]]+\]|\*\*[^*]+\*\*|__[^_]+__)/g;

const variantClassNames: Record<string, string> = {
  lgBlock: styles.lgBlockImg,
  block: styles.blockImg,
  sm: styles.smallImg,
  side: styles.sideImg,
};

export function renderFAQText(text: string): React.ReactNode[] {
  return text.split(PATTERN).map((part, i) => {
    // Image reference: [key] or [key:block] or [key:sm] or [key:lgBlock] or [key:side]
    const imgMatch = part.match(/^\[([a-zA-Z]+)(?::(block|sm|lgBlock|side))?\]$/);
    if (imgMatch) {
      const [, key, variant] = imgMatch;
      const src = faqImages[key as FAQImageKey];
      if (!src) return <span key={i}>[{key}]</span>; // Fallback for missing images

      const className = variant ? variantClassNames[variant] : styles.inlineImg;
      return <img key={i} src={src} alt="" className={className} />;
    }

    // Bold: **text**
    const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
    if (boldMatch) return <strong key={i}>{boldMatch[1]}</strong>;

    // Underline: __text__
    const underlineMatch = part.match(/^__([^_]+)__$/);
    if (underlineMatch) return <u key={i}>{underlineMatch[1]}</u>;

    // Plain text with line breaks
    if (!part) return null;
    return part.split("\n").map((line, j, arr) => (
      <span key={`${i}-${j}`}>
        {line}
        {j < arr.length - 1 && <br />}
      </span>
    ));
  });
}
